import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box, Paper, TextField, Button, Alert, CircularProgress,
  IconButton, InputAdornment, Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { BRAND, TEXT } from '../theme/colors';

const Login = () => {
  const { login } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [infoKey, setInfoKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const from = location.state?.from?.pathname || '/';

  // Surface why we landed here. Set by axios.js on 401, cleared once shown
  // so a manual /login navigation later is silent.
  useEffect(() => {
    let reason = null;
    try {
      reason = localStorage.getItem('terraMindLoginReason');
      if (reason) localStorage.removeItem('terraMindLoginReason');
    } catch (_) {
      // localStorage unavailable in some private modes — non-fatal.
    }
    if (reason === 'session_expired') {
      setInfoKey('sessionExpiredMessage');
    } else if (reason === 'auth_required') {
      setInfoKey('authRequiredMessage');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount — key is stable, t() called at render so it reacts to language changes

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!username.trim() || !password) {
      setError(t('credentialsRequired') || 'Username and password are required');
      return;
    }
    setLoading(true);
    try {
      await login(username.trim(), password);
      // The showcase/kiosk account exists only to play the ΔΕΘ trailer, so send
      // it straight into the demo instead of the map. /showcase is a no-op guard
      // for any other account, but this account IS the seeded one, so it starts
      // the cinema immediately — a visitor (or an attendant re-logging at the
      // stand) lands in the demo with no extra click. Case-insensitive; kept in
      // sync with ShowcaseLauncher.SHOWCASE_ACCOUNT.
      const dest = username.trim().toLowerCase() === 'showcase'
        ? `/showcase?lang=${language}`
        : from;
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || t('loginFailed') || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${BRAND.brownDark} 0%, ${BRAND.green} 50%, ${BRAND.brownMed} 100%)`,
    }}>
      <Paper sx={{ p: 4, maxWidth: 420, width: '100%', borderRadius: 3, boxShadow: '0 8px 40px rgba(0,0,0,0.2)', position: 'relative' }}>
        {/* Language toggle — visible immediately so a Greek farmer who
            sees English by mistake can switch before typing anything */}
        <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
          <Button
            size="small"
            onClick={toggleLanguage}
            variant="outlined"
            sx={{
              minWidth: 0, px: 1.5, py: 0.5, fontSize: '0.8rem', fontWeight: 700,
              borderColor: 'rgba(0,0,0,0.2)', color: TEXT.secondary,
              '&:hover': { borderColor: BRAND.green, color: BRAND.green },
            }}
            aria-label={language === 'el' ? 'Switch to English' : 'Αλλαγή σε Ελληνικά'}
          >
            {language === 'el' ? 'EN' : 'ΕΛ'}
          </Button>
        </Box>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src={language === 'el' ? '/light_gr.png' : '/light_en.png'}
            alt="TerraMind"
            sx={{
              maxWidth: 340,
              height: 90,
              objectFit: 'contain',
              display: 'block',
              mx: 'auto',
              mb: 1.5,
            }}
          />
        </Box>

        {infoKey && !error && (
          <Alert severity="info" sx={{ mb: 2 }}>{t(infoKey)}</Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t('usernameOrEmail') || 'Username or email'}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            sx={{ mb: 2 }}
            autoFocus
            required
            InputProps={{ sx: { fontSize: '0.95rem' } }}
            InputLabelProps={{ sx: { fontSize: '0.95rem' } }}
          />
          <TextField
            fullWidth
            label={t('password') || 'Password'}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{
              mb: 3,
              // Hide the browser's native password-reveal button (Edge/Chrome)
              // so it doesn't appear alongside our custom show/hide toggle.
              '& input::-ms-reveal': { display: 'none' },
              '& input::-ms-clear': { display: 'none' },
              '& input::-webkit-contacts-auto-fill-button': { display: 'none' },
            }}
            required
            InputLabelProps={{ sx: { fontSize: '0.95rem' } }}
            InputProps={{
              sx: { fontSize: '0.95rem' },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                    aria-label={showPassword ? (t('hidePassword') || 'Hide password') : (t('showPassword') || 'Show password')}
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{ py: 1.5, fontSize: '0.95rem', fontWeight: 600, bgcolor: BRAND.green, '&:hover': { bgcolor: BRAND.greenDark } }}
          >
            {loading ? <CircularProgress size={24} /> : (t('login') || 'Login')}
          </Button>
        </form>

        <Typography sx={{ textAlign: 'center', mt: 2, fontSize: '0.9rem' }}>
          <Link to="/forgot-password" style={{ color: TEXT.secondary, display: 'inline-block', padding: '8px 4px' }}>
            {t('forgotPassword') || 'Forgot password?'}
          </Link>
        </Typography>

        {/* Registration link hidden — registration is invite-only.
            Re-enable by uncommenting when ALLOWED_REGISTRATION_EMAILS is populated. */}
        {/* <Typography sx={{ textAlign: 'center', mt: 1.5, fontSize: '0.95rem' }}>
          {t('noAccount') || "Don't have an account?"}{' '}
          <Link to="/register" style={{ color: BRAND.green, fontWeight: 600 }}>
            {t('createAccount') || 'Create Account'}
          </Link>
        </Typography> */}
      </Paper>
    </Box>
  );
};

export default Login;
